import { useCallback, useMemo } from 'react';
import { Callout, Column, KeyboardDatePicker, MenuItem, Row, Select, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { serviceModel } from "../../../models";
import { validateStartBillingAt } from "../../../models/service/logic";
import { AdvancedSettingsControl } from "../AdvancedSettingsControl/AdvancedSettingsControl";
import { AdvancedSetting } from "../ServiceAdvancedSettings.types";
import { getStartAutomaticBillingAtSelection } from "../ServiceAdvancedSettings.utils";
import { useCallOuts } from "./hooks/useCallOuts";
import { useMenuItems } from "./hooks/useMenuItems";
import { useStartBillingDates } from "./hooks/useStartBillingDates";
import styles from "./StartAutomaticBillingAtControl.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export let StartAutomaticBillingAtControlDropdownMenuItem = /*#__PURE__*/function (StartAutomaticBillingAtControlDropdownMenuItem) {
  StartAutomaticBillingAtControlDropdownMenuItem["Pause"] = "Pause";
  StartAutomaticBillingAtControlDropdownMenuItem["SpecificDate"] = "SpecificDate";
  StartAutomaticBillingAtControlDropdownMenuItem["AcceptanceDate"] = "AcceptanceDate";
  return StartAutomaticBillingAtControlDropdownMenuItem;
}({});
export const GetStartAutomaticBillingAtControlTID = parentTestId => getTestIds(parentTestId);
export const StartAutomaticBillingAtControl = _ref => {
  let {
    isReadOnly,
    info,
    label,
    disabled,
    disabledTooltip,
    service,
    relationshipState,
    onPausedChange,
    onBillOnAcceptanceChange,
    onStartBillingAtChange,
    testId
  } = _ref;
  const testIds = useMemo(() => GetStartAutomaticBillingAtControlTID(testId), [testId]);
  const {
    StartAutomaticBillingAtControlTestIds
  } = testIds;
  const startBillingAt = serviceModel.startBillingAt(service);
  const isAmendedService = serviceModel.isFromAmendment(service);
  const relationshipEffectiveDate = relationshipState.effectiveDate;
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const {
    getToday,
    minimumEffectiveDate,
    getInitialEffectiveDate
  } = useStartBillingDates({
    relationshipEffectiveDate,
    isAmendedService
  });
  const menuItems = useMenuItems({
    isBilledUpfront,
    relationshipEffectiveDate,
    getToday,
    isAmendedService
  });
  const selectedItem = useMemo(() => getStartAutomaticBillingAtSelection(service), [service]);
  const isChecked = !!selectedItem;
  const callouts = useCallOuts({
    testIds,
    service,
    relationshipEffectiveDate,
    selectedItem,
    isChecked,
    isAmendedService
  });
  const startBillingAtDatePickerError = useMemo(() => {
    if (selectedItem !== StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate || isReadOnly) {
      return undefined;
    }
    const error = validateStartBillingAt(service, {
      relationshipEffectiveDate,
      now: getToday()
    });
    return error === null || error === void 0 ? void 0 : error[0];
  }, [service, relationshipEffectiveDate, getToday, selectedItem, isReadOnly]);
  const updateServiceByItem = useCallback((item, newValue) => {
    if (item === StartAutomaticBillingAtControlDropdownMenuItem.Pause) {
      return onPausedChange === null || onPausedChange === void 0 ? void 0 : onPausedChange(service.id, newValue);
    } else if (item === StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate) {
      return onBillOnAcceptanceChange === null || onBillOnAcceptanceChange === void 0 ? void 0 : onBillOnAcceptanceChange(service.id, newValue);
    } else if (item === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate) {
      return onStartBillingAtChange === null || onStartBillingAtChange === void 0 ? void 0 : onStartBillingAtChange(service.id, newValue instanceof Date ? newValue : null);
    }
  }, [service, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange]);
  return /*#__PURE__*/_jsx(AdvancedSettingsControl, {
    isReadOnly: isReadOnly,
    info: info,
    label: label,
    checked: isChecked,
    testid: StartAutomaticBillingAtControlTestIds.Root(),
    disabled: disabled,
    disabledTooltip: disabledTooltip,
    onChange: checked => {
      if (checked) {
        updateServiceByItem(StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate, getInitialEffectiveDate());
      } else if (selectedItem) {
        updateServiceByItem(selectedItem, false);
      }
    },
    children: isChecked && /*#__PURE__*/_jsxs(Column, {
      className: styles.automaticBillingAtContent,
      gap: 12,
      children: [/*#__PURE__*/_jsxs(Row, {
        className: styles.automaticBillingAtDropdownMenu,
        alignItems: "start",
        children: [/*#__PURE__*/_jsx(Select, {
          className: styles.dropdownMenu,
          size: "small",
          disabled: isReadOnly,
          "data-testid": StartAutomaticBillingAtControlTestIds.DropdownMenu.Root(),
          value: selectedItem,
          onChange: event => {
            const value = event.target.value;
            if (value === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate) {
              updateServiceByItem(value, getInitialEffectiveDate());
            } else {
              updateServiceByItem(value, true);
            }
          },
          SelectProps: {
            renderValue: value => {
              const selected = menuItems.find(opt => opt.value === value);
              return selected ? selected.text : '';
            }
          },
          children: menuItems.map(item => {
            var _item$tooltip;
            return /*#__PURE__*/_jsx(MenuItem, {
              "data-testid": StartAutomaticBillingAtControlTestIds.DropdownMenu.MenuItem(item.value),
              value: item.value,
              disabled: item.disabled,
              tooltipProps: {
                title: (_item$tooltip = item.tooltip) !== null && _item$tooltip !== void 0 ? _item$tooltip : ''
              },
              variant: "multiline",
              className: styles.menuItem,
              children: /*#__PURE__*/_jsxs(Column, {
                children: [/*#__PURE__*/_jsx(Typography, {
                  weight: "normal",
                  children: item.text
                }), /*#__PURE__*/_jsx(Typography, {
                  children: item.subtext
                })]
              })
            }, item.value);
          })
        }), selectedItem === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate && /*#__PURE__*/_jsx(KeyboardDatePicker, {
          disabled: isReadOnly,
          size: "small",
          minDate: minimumEffectiveDate,
          className: styles.datePicker,
          "data-testid": StartAutomaticBillingAtControlTestIds.StartBillingAtDatePicker(),
          value: startBillingAt,
          error: !!startBillingAtDatePickerError,
          helperText: startBillingAtDatePickerError,
          helperTextReserveSpace: true,
          onChange: date => {
            onStartBillingAtChange === null || onStartBillingAtChange === void 0 || onStartBillingAtChange(service.id, date ? date : null);
          }
        })]
      }), callouts.map(callout => /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": callout.dataTestid,
        children: callout.text
      }, callout.dataTestid))]
    })
  }, AdvancedSetting.StartAutomaticBillingAt);
};